import { useMemo } from "react";
import { PurchaseReportData } from "@snackpass/snackpass-types";

import { useOrders } from "#orders/orders-context";
import {
    useBridgeOrdersData,
    BridgeOrderData,
} from "src/api/bridge/useBridgeOrdersData";
import { useIsBridgeEnabled } from "src/api/bridge/useIsBridgeEnabled";

// Infer the type from useOrders return type
type OrdersContextType = ReturnType<typeof useOrders>;

// Re-export for backward compatibility
export type { BridgeOrderData };

// Enhanced PurchaseReportData type with bridge data
type EnhancedPurchaseReportData = PurchaseReportData & {
    bridge?: BridgeOrderData | null;
};

// Enhanced context type with the same interface but enhanced rows
type EnhancedOrdersContextType = Omit<
    OrdersContextType,
    "purchaseReportRows"
> & {
    purchaseReportRows: {
        data?: EnhancedPurchaseReportData[];
        isLoading: boolean;
    };
    isBridgeDataUsed: boolean;
};

/**
 * Wrapper around useOrders() that populates the purchaseReportRows with bridge data.
 *
 * Maintains the same interface as useOrders() but enhances the purchaseReportRows
 * with additional fields grouped under "bridge" from the bridge SSE endpoint.
 *
 * Usage: Just replace useOrders() with useBridgeEnhancedOrders() in OrdersTable
 */
export const useBridgeEnhancedOrders = (): EnhancedOrdersContextType => {
    const isBridgeEnabled = useIsBridgeEnabled();
    // Get the full original context to pass it through
    const useOrdersContext = useOrders();
    const {
        purchaseReportRows: { data: originalRows, isLoading: rowsLoading },
    } = useOrdersContext;

    // Stringify the order IDs
    const orderIdsString = (
        originalRows?.map((row) => row.purchaseId).filter(Boolean) || []
    ).join(",");

    // Memoize original rows to prevent unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stableOriginalRows = useMemo(() => originalRows, [orderIdsString]);

    // Use shared hook with string
    const { bridgeResponse, bridgeError, bridgeLoading } =
        useBridgeOrdersData(orderIdsString);

    // Merge the data - creating enhanced PurchaseReportData objects
    const enhancedRows: EnhancedPurchaseReportData[] | undefined =
        useMemo(() => {
            // If no original rows, return empty array immediately
            if (!stableOriginalRows || stableOriginalRows.length === 0)
                return [];

            if (bridgeLoading) return undefined;

            if (!isBridgeEnabled) return stableOriginalRows;

            // Fallback to original data if bridge connection failed or no results
            if (bridgeError || !bridgeResponse?.result) {
                if (bridgeError)
                    console.warn(
                        "Bridge API connection failed, showing original orders.",
                        bridgeError,
                    );
                return stableOriginalRows;
            }

            // Log API-level errors but continue processing
            if (bridgeResponse?.errors?.length ?? 0 > 0)
                console.warn(
                    "Bridge API reported errors:",
                    bridgeResponse.errors,
                );

            if (__DEV__) {
                console.log(
                    "Bridge purchases: ",
                    bridgeResponse.result.sort(
                        (a, b) =>
                            Number(b.order.createdAt || "0") -
                            Number(a.order.orderNumber || "0"),
                    ),
                );
            }

            // Create lookup map and enhance rows
            const bridgeMap = bridgeResponse.result.reduce(
                (acc, item) => acc.set(item.mongoID, item),
                new Map<string, BridgeOrderData>(),
            );

            return stableOriginalRows.map((row) => ({
                ...row,
                // override dateReceived with bridge order.createdAt for proper sorting
                dateReceived:
                    bridgeMap.get(row.purchaseId)?.order.createdAt ||
                    row.dateReceived,
                bridge: bridgeMap.get(row.purchaseId) || null,
            }));
        }, [
            isBridgeEnabled,
            stableOriginalRows,
            bridgeLoading,
            bridgeResponse?.result,
            bridgeResponse?.errors,
            bridgeError,
        ]);

    const isLoading =
        rowsLoading ||
        (isBridgeEnabled && bridgeResponse === undefined && !bridgeError) ||
        enhancedRows === undefined;

    const isBridgeDataUsed =
        isBridgeEnabled && !bridgeError && !!bridgeResponse?.result?.length;

    return {
        ...useOrdersContext,
        purchaseReportRows: {
            data: enhancedRows,
            isLoading,
        },
        isBridgeDataUsed,
    };
};
